import {
  DataModel, SerializationContext, SerializedData, DataModelBuilder, isSameData,
} from '../../dto/dto.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { MapLocation } from '../map-location.js';
import { LocalizedString } from '../localized-string.js';
import { VersionRange } from '../../core/version-range.js';
import { Language } from '../language.js';

export class Building extends DataModel {
  static readonly TYPE_ID: string = '9ef3319b515145e9801442b9fe70ec24';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly uniqueId: string,
    public readonly names: LocalizedString,
    public readonly location: MapLocation,
  ) {
    super();
  }

  get $typeId(): string {
    return Building.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(Building.SCHEMA_VERSION);
  }

  // For backward compatibility
  get name(): string {
    return this.names.getAnyOrEmpty();
  }

  static fromMap(context: SerializationContext, data: SerializedData): Building {
    return new Building(
        MapUtils.get<string>(data, 'uniqueId'),
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(data, 'names')),
        context.fromData<MapLocation>(MapUtils.get<SerializedData>(data, 'location')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'names': context.toData(this.names),
      'location': context.toData(this.location),
    };
  }

  clone(): this {
    return new Building(
        this.uniqueId,
        this.names.clone(),
        this.location.clone(),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return this.uniqueId === other.uniqueId &&
        isSameData(this.names, other.names) &&
        isSameData(this.location, other.location);
  }
}

export class BuildingBuilder extends DataModelBuilder<Building> {
  build(context: SerializationContext, map: SerializedData): Building {
    return Building.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(Building.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(Building.SCHEMA_VERSION)) {
      return null;
    }

    let version = versionRange.to;

    // In this version we replace the `name` field of type `string` with the `names` field of type `LocalizedString`.
    if(version < 2) {
      const name = MapUtils.get<string>(map, 'name');
      map['names'] = context.toData(LocalizedString.forLanguage(Language.Hungarian, name));
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }

  migrateFromCurrentSchemaVersion(context: SerializationContext, model: Building, map: SerializedData,
  ): SerializedData {
    map['name'] = model.name;
    map['$schema_version'] = VersionRange.encodedBetween(1, Building.SCHEMA_VERSION);
    return map;
  }
}
